
import { Node, Edge } from "@xyflow/react";
import { toPng, toJpeg } from 'html-to-image';
import jsPDF from 'jspdf';
import { exportToWordHtml } from './wordExport';
import { downloadFile } from './common';
import { toast } from "@/hooks/use-toast";

/**
 * Captures the ReactFlow canvas as an image
 */
const captureFlowchartImage = async (format: 'png' | 'jpeg' = 'png'): Promise<string> => {
  const flowElement = document.querySelector('.react-flow') as HTMLElement;
  if (!flowElement) {
    throw new Error('ReactFlow element not found');
  }

  const dataUrl = format === 'jpeg' 
    ? await toJpeg(flowElement, { 
        quality: 0.95,
        backgroundColor: '#ffffff',
        pixelRatio: 2
      })
    : await toPng(flowElement, {
        backgroundColor: '#ffffff',
        pixelRatio: 2
      });

  return dataUrl;
};

/**
 * Exports flowchart as PDF
 */
export const exportFlowchartToPDF = async (
  nodes: Node[], 
  edges: Edge[], 
  title: string = "Flowchart"
): Promise<void> => {
  try {
    const imageDataUrl = await captureFlowchartImage('png');
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Add title
    pdf.setFontSize(16);
    pdf.text(title, 20, 20);

    // Add flowchart image
    const imgWidth = 250;
    const imgHeight = 150;
    pdf.addImage(imageDataUrl, 'PNG', 20, 30, imgWidth, imgHeight);

    // Add node details on second page if there are many nodes
    if (nodes.length > 0) {
      pdf.addPage();
      pdf.setFontSize(14);
      pdf.text('Node Details:', 20, 20);
      
      let yPosition = 35;
      nodes.forEach((node, index) => {
        if (yPosition > 280) {
          pdf.addPage();
          yPosition = 20;
        }
        
        const nodeText = `${index + 1}. ${node.data?.category || 'Node'}: ${node.data?.label || 'Untitled'}`;
        pdf.setFontSize(10);
        pdf.text(nodeText, 20, yPosition);
        yPosition += 10;
      });
    }

    // Download PDF
    const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flowchart.pdf`;
    pdf.save(fileName);

    toast({
      title: "PDF Export Successful",
      description: `Flowchart exported as ${fileName}`,
      duration: 3000,
    });
  } catch (error) {
    console.error("Failed to export PDF:", error);
    throw error;
  }
};

/**
 * Exports flowchart as JPEG
 */
export const exportFlowchartToJPEG = async (
  nodes: Node[], 
  edges: Edge[], 
  title: string = "Flowchart"
): Promise<void> => {
  try {
    const imageDataUrl = await captureFlowchartImage('jpeg');
    
    // Convert data URL to blob and download
    const response = await fetch(imageDataUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flowchart.jpg`;
    link.click();
    
    URL.revokeObjectURL(url);

    toast({
      title: "JPEG Export Successful",
      description: `Flowchart exported as ${link.download}`,
      duration: 3000,
    });
  } catch (error) {
    console.error("Failed to export JPEG:", error);
    throw error;
  }
};

/**
 * Exports flowchart as Word document with embedded image
 */
export const exportFlowchartToWord = async (
  nodes: Node[], 
  edges: Edge[], 
  title: string = "Flowchart"
): Promise<void> => {
  try {
    const imageDataUrl = await captureFlowchartImage('png');
    
    // Create enhanced HTML for Word with embedded image
    const htmlContent = `
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .flowchart-image { text-align: center; margin: 20px 0; }
          .flowchart-image img { max-width: 100%; height: auto; border: 1px solid #ccc; }
          .node-details { margin-top: 30px; }
          .node-item { margin-bottom: 10px; padding: 10px; border-left: 3px solid #007acc; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${title}</h1>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="flowchart-image">
          <h2>Flowchart Diagram</h2>
          <img src="${imageDataUrl}" alt="Flowchart Diagram" />
        </div>
        
        <div class="node-details">
          <h2>Node Details</h2>
          ${nodes.map((node, index) => `
            <div class="node-item">
              <strong>${index + 1}. ${node.data?.category || 'Node'}</strong><br>
              Content: ${node.data?.label || 'No content'}<br>
              ${node.data?.shape ? `Shape: ${node.data.shape}<br>` : ''}
              Position: (${Math.round(node.position.x)}, ${Math.round(node.position.y)})
            </div>
          `).join('')}
        </div>
        
        ${edges.length > 0 ? `
          <div class="connections">
            <h2>Connections</h2>
            ${edges.map((edge, index) => {
              const sourceNode = nodes.find(n => n.id === edge.source);
              const targetNode = nodes.find(n => n.id === edge.target);
              return `
                <div class="node-item">
                  ${index + 1}. ${sourceNode?.data?.label || 'Unknown'} → ${targetNode?.data?.label || 'Unknown'}
                </div>
              `;
            }).join('')}
          </div>
        ` : ''}
      </body>
      </html>
    `;

    const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_flowchart.html`;
    downloadFile(htmlContent, fileName, 'text/html');
  } catch (error) {
    console.error("Failed to export Word:", error);
    throw error;
  }
};

/**
 * Error handling wrapper for all export functions
 */
export const exportFlowchartWithErrorHandling = async (
  format: 'pdf' | 'jpeg' | 'word',
  nodes: Node[],
  edges: Edge[],
  title: string = "Flowchart"
): Promise<void> => {
  try {
    switch (format) {
      case 'pdf':
        await exportFlowchartToPDF(nodes, edges, title);
        break;
      case 'jpeg':
        await exportFlowchartToJPEG(nodes, edges, title);
        break;
      case 'word':
        await exportFlowchartToWord(nodes, edges, title);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  } catch (error) {
    console.error(`Failed to export flowchart as ${format}:`, error);
    toast({
      title: "Export Failed",
      description: `Could not export flowchart as ${format}. Please try again.`,
      variant: "destructive",
    });
  }
};
